/** Session state for the chart / options workspace. Survives leaving the options table. */

export const OPTIONS_WORKSPACE_STORAGE_KEY = 'pt.optionsWorkspace.v1';
export const MAX_EXPANDED_EXPIRATIONS = 4;

export const DEFAULT_OPTIONS_FILTERS = Object.freeze({
  regular: true,
  weekly: true,
  quarterly: true,
  standard: true,
  nonStandard: false,
});

export function defaultOptionsChainState() {
  return {
    side: 'Both',
    strikeCount: 50,
    filters: { ...DEFAULT_OPTIONS_FILTERS },
    expandedByUnderlying: {},
    expandedOrder: {},
    hiddenColumns: [],
    scrollTop: 0,
  };
}

export function readOptionsWorkspace() {
  const fallback = { view: 'chart', chain: defaultOptionsChainState() };
  if (typeof sessionStorage === 'undefined') return fallback;
  try {
    const raw = sessionStorage.getItem(OPTIONS_WORKSPACE_STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return {
      view: parsed?.view === 'options' ? 'options' : 'chart',
      chain: {
        ...defaultOptionsChainState(),
        ...(parsed?.chain && typeof parsed.chain === 'object' ? parsed.chain : {}),
        filters: {
          ...DEFAULT_OPTIONS_FILTERS,
          ...(parsed?.chain?.filters || {}),
        },
      },
    };
  } catch {
    return fallback;
  }
}

export function writeOptionsWorkspace(view, chain) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(
      OPTIONS_WORKSPACE_STORAGE_KEY,
      JSON.stringify({ view: view === 'options' ? 'options' : 'chart', chain })
    );
  } catch {
    // Private mode or a full quota should not block the chain.
  }
}

export function strikeProximityForCount(strikeCount) {
  const n = Number(strikeCount);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.ceil(n / 2);
}

/**
 * Remember which expiration rows are open, per underlying, up to `max` at once.
 */
export function withExpandedExpiration(chain, underlying, date, open, max = MAX_EXPANDED_EXPIRATIONS) {
  const root = String(underlying || '').toUpperCase();
  const by = { ...(chain?.expandedByUnderlying || {}) };
  const current = { ...(by[root] || {}) };
  const prevOrder = Array.isArray(chain?.expandedOrder?.[root])
    ? chain.expandedOrder[root].filter((d) => current[d])
    : Object.keys(current).filter((d) => current[d]);
  const order = [...prevOrder];

  if (open) {
    current[date] = true;
    const existing = order.indexOf(date);
    if (existing >= 0) order.splice(existing, 1);
    order.push(date);
    while (order.length > max) {
      const drop = order.shift();
      if (drop) current[drop] = false;
    }
  } else {
    current[date] = false;
    const existing = order.indexOf(date);
    if (existing >= 0) order.splice(existing, 1);
  }

  return {
    ...chain,
    expandedByUnderlying: { ...by, [root]: current },
    expandedOrder: { ...(chain?.expandedOrder || {}), [root]: order },
  };
}

export function isExpirationExpanded(chain, underlying, date) {
  const root = String(underlying || '').toUpperCase();
  return chain?.expandedByUnderlying?.[root]?.[date] === true;
}
